import {
	UseQueryResult,
	useQuery,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import {
	organizationService,
	OrganizationRequest,
	OrganizationUpdateRequest,
} from "../../services/organization.service";
import { OrganizationResponse } from "../../domain/organization.interface";

const {
	findOrganizationsByRoleId,
	findOrganizationsByAccountProgramAndParentOrg,
} = organizationService;

export const useGetOrganizationsByRoleId = (
	accountId: string,
	rolId: string,
) => {
	return useQuery({
		queryKey: ["sims-account-phips"],
		queryFn: () => findOrganizationsByRoleId(accountId, rolId),
		staleTime: 1000 * 60 * 60 * 24 * 7,
	});
};

export const useGetOrgsByAccountAndParentOrgId = (
	accountId: string,
	orgId: string,
	programId: string,
) => {
	return useQuery({
		queryKey: ["sims-account-orgs"],
		queryFn: () =>
			findOrganizationsByAccountProgramAndParentOrg(
				accountId,
				orgId,
				programId,
			),
		staleTime: 1000 * 60 * 60 * 24 * 7,
	});
};

export const useGetOrganizationList = (
	accountId: string,
	initialData = [],
): UseQueryResult<OrganizationResponse[]> => {
	const userQuery = useQuery({
		queryKey: ["get-organizaation-list"],
		queryFn: () => organizationService.findAll(),
		initialData,
		staleTime: 0,
	});

	return userQuery;
};

// CRUD hooks for organizations management
export const useOrganizations = () => {
	return useQuery({
		queryKey: ["organizations"],
		queryFn: organizationService.getOrganizations,
	});
};

export const useCreateOrganization = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: OrganizationRequest) =>
			organizationService.createOrganization(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["organizations"] });
		},
	});
};

export const useUpdateOrganization = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: OrganizationUpdateRequest) =>
			organizationService.updateOrganization(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["organizations"] });
		},
	});
};

export const useDeleteOrganization = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => organizationService.deleteOrganization(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["organizations"] });
		},
	});
};
